import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Tooltip,
    IconButton
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { CLTCostBreakdown } from '../../services/cltCalculator';
import { formatCurrency } from '../../utils/format';

interface CLTCostDetailsProps {
    open: boolean;
    onClose: () => void;
    costBreakdown: CLTCostBreakdown;
}

const costExplanations = {
    salarioBase: 'Salário base mensal bruto do funcionário',
    decimoTerceiro: '1/12 do salário base para provisão do 13º',
    feriasProvisao: 'Provisão de 1/3 de férias líquido dividido por 11 meses',
    fgts: '8% sobre (Salário Base + 13º + Férias)',
    encargos: 'Encargos sociais sobre a folha (INSS Patronal, RAT/SAT, INSS Terceiros, Salário Educação)',
    valeTransporte: 'Custo do VT menos desconto de 6% do salário (quando aplicável)',
    valeRefeicao: 'Valor diário × dias úteis',
    valeAlimentacao: 'Valor diário × dias úteis',
    auxilioSaude: 'Valor mensal do plano de saúde',
    custoTotal: 'Soma de todos os custos mensais',
    fatorMultiplicador: 'Custo total dividido pelo salário base'
};

export const CLTCostDetails: React.FC<CLTCostDetailsProps> = ({
    open,
    onClose,
    costBreakdown
}) => {
    const renderRow = (label: string, value: number, key: keyof typeof costExplanations) => (
        <TableRow>
            <TableCell>
                {label}
                <Tooltip title={costExplanations[key]}>
                    <IconButton size="small">
                        <InfoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
            <TableCell align="right">
                {key === 'fatorMultiplicador' 
                    ? value.toFixed(2)
                    : formatCurrency(value)
                }
            </TableCell>
        </TableRow>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Detalhamento do Custo CLT</DialogTitle>
            <DialogContent>
                <TableContainer component={Paper}>
                    <Table>
                        <TableBody>
                            {renderRow('Salário Base', costBreakdown.salarioBase, 'salarioBase')}
                            {renderRow('13º (Provisão)', costBreakdown.decimoTerceiro, 'decimoTerceiro')}
                            {renderRow('Férias (Provisão)', costBreakdown.feriasProvisao, 'feriasProvisao')}
                            {renderRow('FGTS', costBreakdown.fgts, 'fgts')}
                            {renderRow('Encargos', costBreakdown.encargos, 'encargos')}
                            {renderRow('Vale Transporte', costBreakdown.valeTransporte, 'valeTransporte')}
                            {renderRow('Vale Refeição', costBreakdown.valeRefeicao, 'valeRefeicao')}
                            {renderRow('Vale Alimentação', costBreakdown.valeAlimentacao, 'valeAlimentacao')}
                            {renderRow('Auxílio Saúde', costBreakdown.auxilioSaude, 'auxilioSaude')}
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <hr />
                                </TableCell>
                            </TableRow>
                            {renderRow('Custo Total', costBreakdown.custoTotal, 'custoTotal')}
                            {renderRow('Fator Multiplicador', costBreakdown.fatorMultiplicador, 'fatorMultiplicador')}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}; 